import { useNuxtApp, useRuntimeConfig } from 'nuxt/app';
import { webpFallback } from '~~/common/utils';
import { REVIEW_DISABLED } from '~~/common/config/review';
import { CBDS_MODIFICATION_STRATEGY_DISALLOWS_CART_OPERATIONS, CBDS_UNDECIDABLE } from '~~/common/config/CartButtonDisplayStatus';
import type { CartButtonDisplayStatus } from '~~/common/config/CartButtonDisplayStatus';
import { cartButtonDisplayStatusUi } from '~~/common/config/CartButtonDisplayStatusUi';
import { useOrderStore } from '~~/common/stores/order';
import { useProductStore } from '~~/common/stores/product';
import type { ProductData, ProductVariantFe } from '~~/common/stores/product';
import { useDeliveryStore } from '~~/shop/app/stores/delivery';

export const FLAG_REMOVE_ICON = Symbol('FlagRemoveIcon');

export const USED_VARIANT_PROPERTY = 'selectedVariant';

type Product = ProductData | null | undefined;
type Variant = ProductVariantFe | null | undefined;

type ApiFlag = NonNullable<NonNullable<ProductVariantFe['flags']>[number]>;

type DisplayedFlag = Omit<ApiFlag, 'iconUrl'> & {
  iconUrl?: ApiFlag['iconUrl'] | typeof FLAG_REMOVE_ICON;
  awBadgeLongText?: string;
};

export function getProduct (props: { productData?: Product }): Product {
  // this.productData is a prop in component where this mixin is using.
  // eg.: AwProductCard.vue, AwProductDetail.vue
  return ('productData' in props && props.productData) ? props.productData : useProductStore().data;
}

export function getVariant (product: Product): Variant {
  return product?.[USED_VARIANT_PROPERTY];
}

export function getProductId (product: Product) {
  const id = product?.id || product?.selectedVariant?.productId;
  return typeof id === 'number' ? id : NaN;
}

export function getProductName (variant: Variant) {
  const name = variant?.name;
  return typeof name === 'string' ? name : 'undefined';
}

export function getVariantId (variant: Variant) {
  const id = variant?.id;
  return typeof id === 'number' ? id : NaN;
}

export function getEanCode (product: Product) {
  return String(product?.eancode || '');
}

export function getSku (variant: Variant) {
  return String(variant?.sku || '');
}

export function getIsAided (variant: Variant) {
  return !!variant?.aided;
}

export function getBlocks (product: Product, variant: Variant) {
  const config = useRuntimeConfig();
  const details = Array.isArray(variant?.details) ? variant.details : [];
  return {
    showDescriptionBlock: details.includes('description'),
    showParameterListBlock: details.includes('parameterList'),
    showNutritionBlock: details.includes('nutrition'),
    showAllergensBlock: details.includes('allergens'),
    showAllergensDetailedBlock: details.includes('allergensDetailed'),
    showIngredientsBlock: details.includes('ingredients'),
    showIngredientsDetailedBlock: details.includes('ingredientsDetailed'),
    showDocumentsBlock: Number(product?.documents?.length) > 0,
    showDataEraserBlock: Boolean(variant?.isDataEraserNeeded),
    showCetelemBlock: Boolean(product?.creditStatus && config.public.cetelemShopCode),
  };
}

export function getIsInVirtualStock (variant: Variant) {
  return !!variant?.isInVirtualStock;
}

export function getStockInfos (variant: Variant) {
  return Array.isArray(variant?.stockInfos) ? variant.stockInfos : [];
}

export function getProductCategoryId (product: Product) {
  return product?.categoryId;
}

export function getDocuments (product: Product) {
  return product?.documents;
}

export function getAllDisplayedFlags (variant: Variant): DisplayedFlag[] {
  const {
    $awt,
  } = useNuxtApp();
  const flags: DisplayedFlag[] = [];
  if (!__AW_IS_SHOP__ && variant?.aided) {
    flags.push({
      flag: 'aw_aided',
      name: 'Aided',
      iconUrl: FLAG_REMOVE_ICON,
      awBadgeLongText: $awt('awd.common.aided_product'),
    });
  }
  if (!__AW_IS_SHOP__ && getIsInVirtualStock(variant)) {
    flags.push({
      flag: 'aw_kne',
      name: 'KNE',
      iconUrl: FLAG_REMOVE_ICON,
      awBadgeLongText: $awt('awd.common.kne'),
    });
  }
  flags.push(...(variant?.flags || []));
  return flags;
}

export function getThumbImageByVariant (variantParam: Variant) {
  const media = variantParam?.media || {};
  if (media.mainVideo) {
    return {
      videoId: media.mainVideo,
    };
  }
  return webpFallback([
    {
      url: media.listImage || media.mainImage || media.images?.[0] || '',
    },
  ]);
}

function isCartButtonDisplayStatus (status: unknown): status is CartButtonDisplayStatus {
  return typeof status === 'string' && Object.hasOwn(cartButtonDisplayStatusUi, status);
}

export function getShowedStatusMessageFull ({ availability }: { availability?: string }) {
  const {
    getModStrategy,
  } = useOrderStore();
  if (!__AW_IS_SHOP__ && getModStrategy) {
    return cartButtonDisplayStatusUi[CBDS_MODIFICATION_STRATEGY_DISALLOWS_CART_OPERATIONS];
  }

  if (__AW_IS_SHOP__ && !useDeliveryStore().setup && availability === CBDS_UNDECIDABLE) {
    return undefined;
  } else {
    return isCartButtonDisplayStatus(availability) ? cartButtonDisplayStatusUi[availability] : undefined;
  }
}

export function isReviewVisible (reviewable?: string) {
  return reviewable !== REVIEW_DISABLED;
}
